import { motion } from "framer-motion";
import { HUBS, ARCS, IMAGES } from "@/lib/site";

const W = 100;
const H = 60;

function hub(name) {
  return HUBS.find((h) => h.name === name);
}

function arcPath(a, b) {
  const p1 = hub(a);
  const p2 = hub(b);
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const lift = Math.min(18, Math.hypot(p2.x - p1.x, p2.y - p1.y) * 0.55);
  return `M ${p1.x} ${p1.y} Q ${mx} ${my - lift} ${p2.x} ${p2.y}`;
}

export default function WorldMap() {
  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-slate-900/10 bg-[#eef2f8]" data-testid="world-map">
      <img
        src={IMAGES.dottedMap}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-multiply"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#eef2f8]/30 via-transparent to-[#eef2f8]/70" />

      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="relative z-10 h-full w-full"
      >
        <defs>
          <linearGradient id="arcGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#2a93ff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#1e3a5f" stopOpacity="0.2" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {ARCS.map(([a, b], i) => (
          <motion.path
            key={`${a}-${b}`}
            d={arcPath(a, b)}
            fill="none"
            stroke="url(#arcGrad)"
            strokeWidth="0.35"
            strokeLinecap="round"
            filter="url(#glow)"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, delay: 0.3 + i * 0.25, ease: "easeInOut" }}
          />
        ))}

        {HUBS.map((h, i) => (
          <g key={h.name} filter="url(#glow)">
            <circle cx={h.x} cy={h.y} r={h.hq ? 1.1 : 0.8} fill={h.hq ? "#4aa6ff" : "#2a93ff"} />
            <circle
              cx={h.x}
              cy={h.y}
              r={h.hq ? 1.1 : 0.8}
              fill="none"
              stroke={h.hq ? "#4aa6ff" : "#2a93ff"}
              strokeWidth="0.25"
              className="map-ping"
              style={{ transformOrigin: `${h.x}px ${h.y}px`, animationDelay: `${i * 0.4}s` }}
            />
          </g>
        ))}
      </svg>

      <div className="pointer-events-none absolute inset-0 z-20">
        {HUBS.map((h) => (
          <span
            key={h.name}
            className={`absolute -translate-x-1/2 font-mono-x text-[9px] uppercase tracking-[0.15em] md:text-[11px] ${
              h.hq ? "font-semibold text-blue-600" : "text-slate-600"
            }`}
            style={{ left: `${h.x}%`, top: `calc(${(h.y / H) * 100}% + 12px)` }}
          >
            {h.name}
            {h.hq && <span className="ml-1 text-blue-500">· HQ</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
